import { Feature, IFeatureContext, IFeature } from './Feature';
import * as d3 from 'd3';
import { PixoSpatial, PixoPt } from '../Pixospatial';
import { getGeom } from '@turf/invariant';
import { polygon, Geometry, GeometryObject } from '@turf/helpers';

export class PolyFeature extends Feature implements IFeature {
  private polygon: any;
  private segmentGroup: any;
  private pointGroup: any;
  private segments: Array<any>;

  private lastLine: any;
  private ptString = '';
  private dragInfo: any = {};
  public get type(): string {
    return 'polygon';
  }

  constructor(context: IFeatureContext, spatialContext: PixoSpatial = null) {
    super(context, spatialContext);

    this.polygon = context.polygonGroup.append('polygon');
    this.polygon.attr('polygon');
    this.polygon.on('click', () => {
      this.AddVertex(d3.event, 'offsetX', 'offsetY');
    });
    this.segmentGroup = context.segmentGroup.append('g');
    this.pointGroup = context.pointGroup.append('g');

    this.segments = [];
    this.points = [];

    this.lastLine = context.segmentGroup.append('line');
    this.lastLine.attr('class', 'line last-line');
    this.lastLine.attr('data-guid', this.GenerateId());
    this.lastLine.on('click', () => {
      this.AddVertex(d3.event, 'offsetX', 'offsetY');
    });
    this.lookup[this.lastLine.attr('data-guid')] = {
      type: 'line-last',
      ui: this.lastLine
    };
    this.groups = [this.pointGroup, this.segmentGroup];
  }
  AddVertex(ptInfo, xProp: string = 'x', yProp: string = 'y') {
    const lastSelect = this.lookup[this.lastSelection];
    if (lastSelect?.type === 'point') {
      const firstPointId = this.points[0].id;
      const lastPointId = this.points[this.points.length-1]?.id;
      if ((this.points.length > 0)) {
        if ([firstPointId, lastPointId].indexOf(lastSelect.id) === -1) {
          const seg = lastSelect.nextSeg();
          if (seg) {
            this.Select(seg.id);
            const newPt = seg.AddVertexFromCoord(ptInfo);
            this.Select(newPt.id);
            return;
          }

        }
      }
    }
    const pt: any = this.MakeVertex(this.points, this.pointGroup, ptInfo, xProp, yProp);

    let fromPt = null;
    let toPt = null;
    let seg = null;
    if (!!this.lastSelection) {
      if (!!this.lookup[this.lastSelection]) {
        const lastSelect = this.lookup[this.lastSelection];
        if (lastSelect.type === 'point') {

          if (this.segments.length > 0) {
            const firstVertex = this.segments[0].from;
            let ending = null;

            if ((firstVertex.id === lastSelect.id)) {
              fromPt = pt;
              toPt = lastSelect;
              ending = 'start';
              this.points.unshift(this.points.pop());
            } else {
              fromPt = this.segments[this.segments.length - 1].to;
              toPt = pt;
              ending = 'end';
            }

            seg = this.MakeSegment(this.points, this.pointGroup, this.segments, this.segmentGroup, fromPt, toPt, ending);
            this.Select(pt.id);
            this.UpdatePolygon(pt, ending);
            this.UpdateLastLine();
            return;
          }
        }
      }
    }

    seg = this.MakeSegment(this.points, this.pointGroup, this.segments, this.segmentGroup);
    this.Select(pt.id);
    this.UpdatePolygon(pt);
    this.UpdateLastLine();
    return pt;
  }
  Remove(): void {
    this.RemovePoints(this.points);
    this.polygon.remove();
    this.lastLine.remove();
  }

  HandleVertexDrag(event): any {
    const ptData = super.HandleVertexDrag(event);
    if (!(!!this.dragInfo.pt)) {
      this.dragInfo.pt = null;
      this.dragInfo.ptI = null;
    }
    if (this.dragInfo.pt !== ptData) {
      this.dragInfo.pt = ptData;
      this.dragInfo.ptI = this.points.indexOf(ptData);
    }

    this.UpdateLastLine();
    const polyPts = this.ptString.split(' ');

    polyPts[this.dragInfo.ptI] = ptData.x + ',' + ptData.y;
    this.ptString = polyPts.join(' ');
    this.polygon.attr('points', this.ptString);
    return ptData;
  }
  UpdatePolygon(pt, ending = 'end') {
    let ptStrPrefix = (this.ptString === '') ? '' : ' ';
    if (ending === 'start') {
      ptStrPrefix = '';
      this.ptString = pt.x + ',' + pt.y + ' ' + this.ptString;
    } else {
      this.ptString += ptStrPrefix + pt.x + ',' + pt.y;
    }
    this.polygon.attr('points', this.ptString);
  }
  UpdateLastLine() {
    if (this.points.length > 2) {
      const lastPt = this.segments[this.segments.length - 1].to;
      if (lastPt) {
        this.lastLine.attr('x1', lastPt.x);
        this.lastLine.attr('y1', lastPt.y);
        this.lastLine.attr('x2', this.segments[0].from.x);
        this.lastLine.attr('y2', this.segments[0].from.y);
        this.lastLine.classed('hidden', false);
      }
    } else {
      this.lastLine.classed('hidden', true);
    }
  }

  ToTurf(): any {
    const turfable = polygon(this.GetCoordinates(this.points));
  }
  PointRemoved(pt) {
    const pts = this.ptString.split(' ');
    const pointI = this.points.indexOf(pt);
    if (pointI > -1) {
      this.points.splice(pointI, 1);
    }
    const ptI = pts.indexOf(pt.x + ',' + pt.y);
    if (ptI > -1) {
      pts.splice(ptI, 1);
      this.ptString = pts.join(' ');

    }
    this.polygon.attr('points', this.ptString);
    this.UpdateLastLine();
  }
  PointUpdated(pt: any, oldX: number, oldY: number) {
    const pts = this.ptString.split(' ');

    const ptI = pts.indexOf(oldX + ',' + oldY);
    if (ptI > -1) {
      pts.splice(ptI, 1, pt.x + ',' + pt.y);
      this.ptString = pts.join(' ');
      this.polygon.attr('points', this.ptString);
    }
    this.UpdateLastLine();
  }
  VertexAddedTo(pt, toSeg) {
    const polyPoints = this.ptString.split(' ');
    const fromPtI = polyPoints.indexOf(toSeg.from.x + ',' + toSeg.from.y);
    polyPoints.splice(fromPtI + 1, 0, pt.x + ',' + pt.y);
    this.ptString = polyPoints.join(' ');
    this.polygon.attr('points', this.ptString);
    this.UpdateLastLine();
    this.Select(pt.id);
  }
  ToGeoJSONGeom(): GeometryObject | false {
    if (this.points.length < 1) {
      return null;
    }
    const coords = [];
    for (const pt of this.points) {
      const coord = pt.coord.spatial;
      coords.push([coord.lng, coord.lat]);
    }
    coords.push(coords[0]);
    if (coords.length < 4) {
      return false;
    }
    return getGeom(polygon([coords]));
  }
  FromGeoJSONGeom(geom: Geometry) {
    if (!geom) {
      return;
    }
    const coords: any = geom.coordinates;


    for (const poly of coords) {

      for (const polyCoord of poly) {
        if (polyCoord === poly[(poly as []).length - 1]) { continue; }
        const ptInfo = this.spatialContext.PixoSpatialCoord(this.spatialContext.SpatialCoord({ lng: polyCoord[0], lat: polyCoord[1] }));
        this.AddVertex(ptInfo);
      }
    }

  }
  ToggleVis(isVis?: boolean) {
    let wasVis = !this.pointGroup.node().classList.contains('hidden');
    isVis = (isVis !== undefined) ? isVis : !wasVis;
    this.pointGroup.node().classList.toggle('hidden', !isVis);
    this.segmentGroup.node().classList.toggle('hidden', !isVis);
  }
  GetVis(): boolean {
    return !this.pointGroup.node().classList.contains('hidden');
  }
}
